<template>
  <div class="price-tags-page">
    <header class="price-tags-page__header">
      <h1>{{ $t('catalog.priceTags.title') }}</h1>
      <p>{{ $t('catalog.priceTags.subtitle') }}</p>
    </header>

    <div class="price-tags-page__layout">
      <section class="price-tags-card price-tags-settings">
        <div class="price-tags-mode">
          <button
            type="button"
            class="price-tags-mode__btn"
            :class="{ 'is-active': priceMode === 'sale' }"
            @click="priceMode = 'sale'"
          >
            <i class="mdi mdi-sale"></i>
            {{ $t('catalog.priceTags.modeSale') }}
          </button>
          <button
            type="button"
            class="price-tags-mode__btn"
            :class="{ 'is-active': priceMode === 'regular' }"
            @click="priceMode = 'regular'"
          >
            <i class="mdi mdi-tag-outline"></i>
            {{ $t('catalog.priceTags.modeRegular') }}
          </button>
        </div>

        <v-select
          v-model="categoryId"
          :items="categoryOptions"
          item-text="name"
          item-value="id"
          outlined
          dense
          hide-details
          :label="$t('catalog.common.category')"
          class="price-tags-settings__field"
        />

        <v-text-field
          v-model="search"
          outlined
          dense
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          :placeholder="$t('catalog.common.searchByName')"
          class="price-tags-settings__field"
        />

        <div class="price-tags-list">
          <div class="price-tags-list__toolbar">
            <span>{{ $t('catalog.priceTags.selected', { count: selectedCount }) }}</span>
            <button type="button" @click="toggleVisible">
              {{ allVisibleSelected ? $t('catalog.priceTags.clearVisible') : $t('catalog.priceTags.selectVisible') }}
            </button>
          </div>

          <div v-if="loading" class="price-tags-list__empty">{{ $t('common.loading') }}</div>
          <div v-else-if="!products.length" class="price-tags-list__empty">{{ $t('catalog.products.noResults') }}</div>
          <label v-for="product in products" :key="product.id" class="price-tags-item">
            <input type="checkbox" :checked="isSelected(product.id)" @change="toggleProduct(product)">
            <div class="price-tags-item__body">
              <strong>{{ product.name }}</strong>
              <span>
                <s v-if="hasDiscount(product)">{{ formatMoney(product.price) }} ₸</s>
                <em>{{ formatMoney(salePrice(product)) }} ₸</em>
                <b v-if="hasDiscount(product)">−{{ discountLabel(product) }}</b>
              </span>
            </div>
            <div class="price-tags-item__tools" @click.stop>
              <input
                v-if="isSelected(product.id)"
                class="price-tags-item__kg"
                :value="weightText(product.id)"
                inputmode="decimal"
                :placeholder="$t('catalog.products.unitKg')"
                @change="setWeight(product.id, $event.target.value)"
              >
              <button
                type="button"
                class="price-tags-item__scale"
                :disabled="scaleLoadingId === product.id"
                :title="$t('catalog.priceTags.readWeight')"
                @click="readWeight(product)"
              >
                <i class="mdi" :class="scaleLoadingId === product.id ? 'mdi-loading mdi-spin' : 'mdi-scale'"></i>
                {{ $t('catalog.priceTags.weight') }}
              </button>
              <div v-if="isSelected(product.id)" class="price-tags-item__copies">
                <button type="button" :disabled="copiesOf(product.id) <= 1" @click="changeCopies(product.id, -1)">−</button>
                <input :value="copiesOf(product.id)" type="number" min="1" max="99" @change="setCopies(product.id, $event.target.value)">
                <button type="button" :disabled="copiesOf(product.id) >= 99" @click="changeCopies(product.id, 1)">+</button>
              </div>
            </div>
          </label>
        </div>

        <v-pagination
          v-if="pageCount > 1"
          v-model="page"
          :length="pageCount"
          :total-visible="5"
          color="#0b4f8a"
        />

        <v-select
          v-model="labelSize"
          :items="labelSizeOptions"
          item-text="label"
          item-value="value"
          outlined
          dense
          hide-details
          :label="$t('catalog.products.labelSize')"
          class="price-tags-settings__field"
        />

        <v-switch
          v-model="showName"
          inset
          dense
          hide-details
          class="price-tags-settings__switch"
          :label="$t('catalog.priceTags.showName')"
        />
        <v-switch
          v-model="showBarcode"
          inset
          dense
          hide-details
          class="price-tags-settings__switch"
          :label="$t('catalog.priceTags.showBarcode')"
        />
        <v-switch
          v-if="priceMode === 'sale'"
          v-model="showOldPrice"
          inset
          dense
          hide-details
          class="price-tags-settings__switch"
          :label="$t('catalog.priceTags.showOldPrice')"
        />

        <div class="price-tags-actions">
          <button
            type="button"
            class="price-tags-btn price-tags-btn--ghost"
            :disabled="scaleLoadingId === 'main'"
            @click="readScaleForSelection"
          >
            <i class="mdi" :class="scaleLoadingId === 'main' ? 'mdi-loading mdi-spin' : 'mdi-scale'"></i>
            {{ $t('catalog.priceTags.weight') }}
          </button>
          <button
            type="button"
            class="price-tags-btn price-tags-btn--ghost"
            :disabled="!labels.length"
            :title="$t('catalog.barcodes.browserPrintHint')"
            @click="printInBrowser"
          >
            <i class="mdi mdi-file-pdf-box"></i>
            {{ $t('catalog.barcodes.browserPrint') }}
          </button>
          <button type="button" class="price-tags-btn" :disabled="!labels.length || printing" @click="printLabels">
            <i :class="printing ? 'mdi mdi-loading mdi-spin' : 'mdi mdi-printer-outline'"></i>
            {{ $t('catalog.barcodes.print', { count: labels.length }) }}
          </button>
        </div>
      </section>

      <section class="price-tags-card price-tags-preview">
        <div class="price-tags-preview__head">
          <h2>{{ $t('catalog.barcodes.preview') }}</h2>
          <span v-if="labels.length">
            {{ $t('catalog.barcodes.summary', { labels: labels.length, pages: pages.length }) }}
          </span>
        </div>

        <div v-if="labels.length" ref="printable" class="price-tags-preview__pages">
          <section
            v-for="(pageItems, pageIndex) in pages"
            :key="pageIndex"
            class="price-sheet"
            :class="labelFormat.roll ? 'price-sheet--roll' : 'price-sheet--a4'"
            :style="sheetStyle"
          >
            <article
              v-for="label in pageItems"
              :key="label.id"
              class="price-label"
              :class="{ 'has-sale': label.showOld }"
            >
              <strong v-if="showName" class="price-label__name">{{ label.name }}</strong>
              <span v-if="label.weight" class="price-label__unit">
                {{ $t('catalog.priceTags.perKg', { price: formatMoney(label.unitPrice) }) }}
              </span>
              <span v-if="label.weight" class="price-label__weight">{{ formatWeight(label.weight) }}</span>
              <div class="price-label__prices">
                <s v-if="label.showOld">{{ formatMoney(label.oldPrice) }} ₸</s>
                <b>{{ formatMoney(label.price) }} ₸</b>
              </div>
              <div v-if="showBarcode && label.barcode" class="price-label__barcode">
                <svg :ref="`tagBarcode${label.id}`"></svg>
              </div>
            </article>
          </section>
        </div>

        <div v-else class="price-tags-preview__empty">
          <i class="mdi mdi-tag-text-outline"></i>
          <p>{{ $t('catalog.priceTags.emptyTitle') }}</p>
          <span>{{ $t('catalog.priceTags.emptyHint') }}</span>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import JsBarcode from 'jsbarcode'
import { getCategories } from '@/api/categories'
import { getProducts } from '@/api/products'
import { tryPrintBarcodeLabels } from '@/api/printer'
import { peekLocalWeight, readScaleWeight } from '@/api/scale'
import { LABEL_FORMATS, labelPageHeight } from '@/utils/labelFormats'
import { notify, notifyError } from '@/utils/notify'

const PRICE_LABEL_FORMATS = {
  single30x20: { width: 30, height: 20, columns: 1, rows: 1, gap: 0, sheetWidth: 30, roll: true },
  single40x30: { width: 40, height: 30, columns: 1, rows: 1, gap: 0, sheetWidth: 40, roll: true },
  single58x30: { width: 58, height: 30, columns: 1, rows: 1, gap: 0, sheetWidth: 58, roll: true },
  single58x40: { width: 58, height: 40, columns: 1, rows: 1, gap: 0, sheetWidth: 58, roll: true },
  ...LABEL_FORMATS,
}

export default {
  name: 'PriceTags',
  data() {
    return {
      search: '',
      page: 1,
      pageCount: 0,
      categoryId: null,
      categories: [],
      products: [],
      selected: {},
      loading: false,
      priceMode: 'sale',
      showName: true,
      showBarcode: true,
      showOldPrice: true,
      labelSize: 'single58x40',
      labelGap: 2,
      printing: false,
      scaleLoadingId: null,
      lastWeighId: null,
    }
  },
  computed: {
    categoryOptions() {
      return [{ id: null, name: this.$t('catalog.common.all') }].concat(this.categories)
    },
    selectedCount() {
      return Object.keys(this.selected).length
    },
    allVisibleSelected() {
      return this.products.length > 0 && this.products.every((product) => this.selected[product.id])
    },
    labelSizeOptions() {
      return [
        { value: 'single58x40', label: this.$t('catalog.barcodes.labelSizeSingle58x40') },
        { value: 'single58x30', label: this.$t('catalog.barcodes.labelSizeSingle58x30') },
        { value: 'single40x30', label: this.$t('catalog.barcodes.labelSizeSingle40x30') },
        { value: 'single30x20', label: this.$t('catalog.barcodes.labelSizeSingle30x20') },
        { value: 'roll30x25', label: this.$t('catalog.products.labelSizeRoll30x25') },
        { value: 'roll30x20', label: this.$t('catalog.products.labelSizeRoll30x20') },
        { value: 'standard', label: this.$t('catalog.products.labelSizeStandard') },
        { value: 'large', label: this.$t('catalog.products.labelSizeLarge') },
        { value: 'compact', label: this.$t('catalog.products.labelSizeCompact') },
      ]
    },
    labelFormat() {
      return PRICE_LABEL_FORMATS[this.labelSize] || PRICE_LABEL_FORMATS.single58x40
    },
    labels() {
      const items = []
      Object.values(this.selected).forEach((product) => {
        const copies = Math.max(1, Number(product.copies) || 1)
        const unitOld = Number(product.price) || 0
        const unitPrice = this.priceMode === 'regular' ? unitOld : this.salePrice(product)
        const weight = Number(product.weight) || 0
        const price = weight > 0 ? Math.round(unitPrice * weight * 100) / 100 : unitPrice
        const oldPrice = weight > 0 ? Math.round(unitOld * weight * 100) / 100 : unitOld
        const showOld = this.priceMode === 'sale' && this.showOldPrice && this.hasDiscount(product)
        for (let index = 0; index < copies; index += 1) {
          items.push({
            id: `${product.id}-${index}`,
            productId: product.id,
            name: product.name,
            barcode: product.barcode || '',
            unitPrice,
            oldPrice,
            price,
            weight,
            showOld,
          })
        }
      })
      return items
    },
    agentPayload() {
      const format = this.labelFormat
      return {
        kind: 'price',
        format: 'code128',
        show_digits: false,
        scale: 1,
        label: {
          width: format.width,
          height: format.height,
          columns: format.columns,
          column_gap: format.columns > 1 ? format.gap || 0 : 0,
          gap: this.labelGap,
        },
        items: this.labels.map((label) => ({
          name: label.name,
          price: label.price,
          old_price: label.showOld ? label.oldPrice : null,
          unit_price: label.weight > 0 ? label.unitPrice : null,
          weight: label.weight || null,
          unit: 'кг',
          barcode: this.showBarcode ? label.barcode : '',
          show_name: this.showName,
          show_barcode: this.showBarcode && !!label.barcode,
          show_old: label.showOld,
        })),
      }
    },
    pages() {
      const perPage = Math.max(1, this.labelFormat.columns * this.labelFormat.rows)
      const pages = []
      for (let index = 0; index < this.labels.length; index += perPage) {
        pages.push(this.labels.slice(index, index + perPage))
      }
      return pages
    },
    sheetStyle() {
      const format = this.labelFormat
      return {
        '--label-columns': format.columns,
        '--label-width': `${format.width}mm`,
        '--label-height': `${format.height}mm`,
        '--label-gap': `${format.gap || 0}mm`,
        '--sheet-width': `${format.sheetWidth}mm`,
        '--sheet-height': `${labelPageHeight(format)}mm`,
      }
    },
  },
  watch: {
    page() {
      this.loadProducts()
    },
    categoryId() {
      this.resetToFirstPage()
    },
    search() {
      this.debouncedSearch()
    },
    labels() {
      this.$nextTick(this.renderBarcodes)
    },
    showBarcode() {
      this.$nextTick(this.renderBarcodes)
    },
    labelSize() {
      this.$nextTick(this.renderBarcodes)
    },
  },
  mounted() {
    this.loadCategories()
    this.loadProducts()
  },
  methods: {
    formatMoney(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 })
    },
    formatWeight(value) {
      const n = Number(value)
      if (!Number.isFinite(n) || n <= 0) return ''
      return `${n.toLocaleString('ru-RU', { maximumFractionDigits: 3 })} ${this.$t('catalog.products.unitKg')}`
    },
    parseWeight(value) {
      const n = Number(String(value || '').replace(',', '.').replace(/\s/g, ''))
      if (!Number.isFinite(n) || n <= 0) return 0
      return Math.round(n * 1000) / 1000
    },
    weightOf(id) {
      return Number(this.selected[id] && this.selected[id].weight) || 0
    },
    weightText(id) {
      const weight = this.weightOf(id)
      return weight > 0 ? String(weight) : ''
    },
    hasDiscount(product) {
      return !!(product && product.product_discount_type && Number(product.product_discount_value) > 0)
    },
    salePrice(product) {
      const price = Number(product && product.price) || 0
      if (!this.hasDiscount(product)) return price
      const value = Number(product.product_discount_value) || 0
      const cut = product.product_discount_type === 'percent'
        ? price * (value / 100)
        : value
      return Math.max(0, Math.round((price - cut) * 100) / 100)
    },
    discountLabel(product) {
      if (!this.hasDiscount(product)) return ''
      return product.product_discount_type === 'percent'
        ? `${Number(product.product_discount_value)}%`
        : `${this.formatMoney(product.product_discount_value)} ₸`
    },
    isSelected(id) {
      return !!this.selected[id]
    },
    copiesOf(id) {
      return (this.selected[id] && this.selected[id].copies) || 1
    },
    toggleProduct(product) {
      if (this.selected[product.id]) {
        this.$delete(this.selected, product.id)
        if (this.lastWeighId === product.id) this.lastWeighId = null
        return
      }
      this.lastWeighId = product.id
      this.$set(this.selected, product.id, { ...product, copies: 1, weight: null })
    },
    setWeight(id, value) {
      const item = this.selected[id]
      if (!item) return
      const weight = this.parseWeight(value)
      this.$set(item, 'weight', weight || null)
    },
    ensureSelected(product) {
      if (this.selected[product.id]) return this.selected[product.id]
      const next = { ...product, copies: 1, weight: null }
      this.$set(this.selected, product.id, next)
      return next
    },
    async applyScaleWeight(product, loadingId = product.id) {
      this.scaleLoadingId = loadingId
      this.lastWeighId = product.id
      try {
        let weight = await peekLocalWeight()
        if (!weight || !(Number(weight.value) > 0)) {
          weight = await readScaleWeight()
        }
        const value = this.parseWeight(weight && weight.value)
        if (!value) {
          notify(this.$t('catalog.priceTags.weightEmpty'), 'warning')
          return
        }
        const item = this.ensureSelected(product)
        this.$set(item, 'weight', value)
        notify(this.$t('catalog.priceTags.weightRead', { value: this.formatWeight(value) }))
      } catch (error) {
        notifyError(error, this.$t('catalog.priceTags.weightReadFailed'))
      } finally {
        this.scaleLoadingId = null
      }
    },
    readWeight(product) {
      if (!product || this.scaleLoadingId) return
      return this.applyScaleWeight(product, product.id)
    },
    readScaleForSelection() {
      if (this.scaleLoadingId) return
      const selected = this.selected[this.lastWeighId]
        || Object.values(this.selected)[0]
        || null
      if (!selected) {
        notify(this.$t('catalog.priceTags.weightNeedProduct'), 'warning')
        return
      }
      return this.applyScaleWeight(selected, 'main')
    },
    toggleVisible() {
      if (this.allVisibleSelected) {
        this.products.forEach((product) => this.$delete(this.selected, product.id))
        return
      }
      this.products.forEach((product) => {
        if (!this.selected[product.id]) {
          this.$set(this.selected, product.id, { ...product, copies: 1 })
        }
      })
    },
    changeCopies(id, step) {
      const item = this.selected[id]
      if (!item) return
      this.$set(item, 'copies', Math.min(99, Math.max(1, (Number(item.copies) || 1) + step)))
    },
    setCopies(id, value) {
      const item = this.selected[id]
      if (!item) return
      this.$set(item, 'copies', Math.min(99, Math.max(1, Math.round(Number(value) || 1))))
    },
    resetToFirstPage() {
      if (this.page !== 1) {
        this.page = 1
        return
      }
      this.loadProducts()
    },
    debouncedSearch: debounce(function () {
      this.resetToFirstPage()
    }, 400),
    async loadCategories() {
      try {
        const list = await getCategories()
        this.categories = Array.isArray(list) ? list : (list && list.data) || []
      } catch (error) {
        this.categories = []
      }
    },
    async loadProducts() {
      this.loading = true
      try {
        const params = { page: this.page, per_page: 24, stock: 'stock' }
        if (this.search) params.search = this.search
        if (this.categoryId) params.category_id = this.categoryId
        const data = await getProducts(params)
        this.products = data.data || []
        this.pageCount = Number(data.last_page) || 0
      } catch (error) {
        notifyError(error)
      } finally {
        this.loading = false
      }
    },
    renderBarcodes() {
      if (!this.showBarcode) return
      this.labels.forEach((label) => {
        if (!label.barcode) return
        const ref = this.$refs[`tagBarcode${label.id}`]
        const svg = Array.isArray(ref) ? ref[0] : ref
        if (!svg) return
        try {
          JsBarcode(svg, String(label.barcode), {
            format: 'CODE128',
            displayValue: false,
            margin: 0,
            height: 28,
            width: 1.2,
          })
        } catch (error) {
          // skip invalid barcode
        }
      })
    },
    printCss() {
      const format = this.labelFormat
      const page = format.roll
        ? `@page{size:${format.sheetWidth}mm ${labelPageHeight(format)}mm;margin:0}`
        : '@page{size:A4;margin:8mm}'
      return `${page}`
        + '*{box-sizing:border-box}html,body{margin:0;padding:0;background:#fff;font-family:Arial,sans-serif;color:#152033}'
        + '.price-sheet{display:grid;width:var(--sheet-width);min-height:var(--sheet-height);grid-template-columns:repeat(var(--label-columns),var(--label-width));grid-auto-rows:var(--label-height);gap:var(--label-gap);align-content:start;page-break-after:always}'
        + '.price-sheet:last-child{page-break-after:auto}'
        + '.price-label{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1mm;padding:2mm;overflow:hidden;text-align:center;border:1px dashed #9fb0c1}'
        + '.price-sheet--roll .price-label{border:0}'
        + '.price-label__name{display:-webkit-box;overflow:hidden;max-width:100%;font-size:12px;font-weight:700;line-height:1.15;-webkit-box-orient:vertical;-webkit-line-clamp:2}'
        + '.price-label__unit,.price-label__weight{display:block;color:#405168;font-size:10px;line-height:1.2}'
        + '.price-label__prices{display:flex;align-items:baseline;justify-content:center;gap:1mm}'
        + '.price-label s{color:#6b7a90;font-size:9px}'
        + '.price-label b{color:#b42318;font-size:18px;font-weight:800;line-height:1}'
        + '.price-label.has-sale b{color:#b42318}'
        + '.price-label__barcode{width:100%} .price-label__barcode svg{display:block;width:100%;height:10mm}'
        + '@media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}'
    },
    async printLabels() {
      if (!this.labels.length || this.printing) return
      if (!this.labelFormat.roll) {
        this.printInBrowser()
        return
      }

      this.printing = true
      try {
        const printed = await tryPrintBarcodeLabels(this.agentPayload)
        if (printed) {
          notify(this.$t('catalog.barcodes.sentToPrinter', { count: this.labels.length }))
          return
        }
        notify(this.$t('catalog.barcodes.agentOffline'), 'warning')
        this.printInBrowser()
      } catch (error) {
        notifyError(error, this.$t('catalog.barcodes.printFailed'))
      } finally {
        this.printing = false
      }
    },
    printInBrowser() {
      const source = this.$refs.printable
      if (!source || !this.labels.length) return
      const printWindow = window.open('', '_blank', 'width=960,height=720')
      if (!printWindow) {
        notify(this.$t('catalog.barcodes.popupBlocked'), 'warning')
        return
      }
      printWindow.document.write(`<!doctype html><html><head><title>Sauda prices</title><style>${this.printCss()}</style></head><body>${source.innerHTML}</body></html>`)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
    },
  },
}
</script>

<style scoped lang="scss">
.price-tags-page {
  --ink: #152033;
  --muted: #6b7a90;
  --accent: #0b4f8a;
  --line: #d9e2ec;
  padding: 28px 32px 40px;
  color: var(--ink);
}

.price-tags-page__header {
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
  }

  p {
    margin: 6px 0 0;
    color: var(--muted);
    font-size: 14px;
  }
}

.price-tags-page__layout {
  display: grid;
  grid-template-columns: minmax(300px, 420px) minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.price-tags-card {
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: #fff;
}

.price-tags-settings {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.price-tags-mode {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: #eef3f7;
}

.price-tags-mode__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 8px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #405168;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &.is-active {
    background: #fff;
    color: var(--accent);
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
  }
}

.price-tags-list {
  max-height: 360px;
  overflow: auto;
  border: 1px solid var(--line);
  border-radius: 12px;
}

.price-tags-list__toolbar,
.price-tags-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
}

.price-tags-list__toolbar {
  position: sticky;
  top: 0;
  justify-content: space-between;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 700;
  z-index: 1;

  button {
    border: 0;
    background: none;
    color: var(--accent);
    cursor: pointer;
  }
}

.price-tags-list__empty {
  padding: 20px 12px;
  color: var(--muted);
  text-align: center;
}

.price-tags-item {
  border-top: 1px solid #eef3f8;
  cursor: pointer;

  input {
    margin: 0;
  }
}

.price-tags-item__body {
  min-width: 0;
  flex: 1;

  strong {
    display: block;
    font-size: 13px;
    line-height: 1.3;
  }

  span {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: baseline;
    color: var(--muted);
    font-size: 12px;
  }

  em {
    font-style: normal;
    color: var(--ink);
    font-weight: 700;
  }

  b {
    color: #b42318;
    font-size: 11px;
  }
}

.price-tags-item__tools {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.price-tags-item__kg {
  width: 52px;
  height: 26px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  text-align: center;
  font-size: 12px;
}

.price-tags-item__scale {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 8px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #eef4fa;
  color: var(--accent);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
}

.price-tags-item__copies {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--line);
  border-radius: 8px;
  overflow: hidden;

  button,
  input {
    width: 28px;
    height: 26px;
    border: 0;
    background: #fff;
    text-align: center;
  }

  input {
    width: 34px;
    border-left: 1px solid var(--line);
    border-right: 1px solid var(--line);
  }
}

.price-tags-settings__switch {
  margin: 0 !important;
}

.price-tags-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.price-tags-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 42px;
  border: 0;
  border-radius: 12px;
  background: var(--accent);
  color: #fff;
  font-weight: 700;
  cursor: pointer;

  &--ghost {
    background: #eef4fa;
    color: var(--accent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.price-tags-preview__head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;

  h2 {
    margin: 0;
    font-size: 18px;
  }

  span {
    color: var(--muted);
    font-size: 13px;
  }
}

.price-tags-preview__pages {
  overflow: auto;
}

.price-sheet {
  display: grid;
  width: var(--sheet-width);
  min-height: var(--sheet-height);
  grid-template-columns: repeat(var(--label-columns), var(--label-width));
  grid-auto-rows: var(--label-height);
  gap: var(--label-gap);
  margin-bottom: 16px;
  background: #fff;
}

.price-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 4px;
  overflow: hidden;
  text-align: center;
  border: 1px dashed #9fb0c1;
}

.price-sheet--roll .price-label {
  border-color: #e4ebf2;
}

.price-label__name {
  display: -webkit-box;
  overflow: hidden;
  max-width: 100%;
  font-size: 12px;
  line-height: 1.15;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.price-label__unit,
.price-label__weight {
  color: #405168;
  font-size: 10px;
  line-height: 1.2;
}

.price-label__prices {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.price-label s {
  color: #6b7a90;
  font-size: 10px;
}

.price-label b {
  color: #b42318;
  font-size: 18px;
  font-weight: 800;
}

.price-label__barcode {
  width: 100%;

  svg {
    display: block;
    width: 100%;
    height: 22px;
  }
}

.price-tags-preview__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  color: var(--muted);
  text-align: center;

  i {
    font-size: 42px;
    margin-bottom: 8px;
  }

  p {
    margin: 0;
    color: var(--ink);
    font-weight: 700;
  }
}

@media (max-width: 960px) {
  .price-tags-page {
    padding: 18px 14px 28px;
  }

  .price-tags-page__layout {
    grid-template-columns: 1fr;
  }
}
</style>
